import { PlayCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { courseApi, type MyCourse } from '../../services/courseApi';
import { getLocalizedTitle } from '../../utils/activityTransform';

// Dashboard tab listing the courses the current user owns (CourseEntitlement).
export default function MyCoursesTab() {
  const { t, i18n } = useTranslation('dashboard');
  const { getToken } = useAuth();
  const [courses, setCourses] = useState<MyCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const data = await courseApi.listMyCourses(token);
        if (!cancelled) setCourses(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load your courses');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  if (loading) {
    return <p className="py-8 text-center text-gray-500">{t('loading')}</p>;
  }
  if (error) {
    return <p className="py-8 text-center text-red-600">{error}</p>;
  }
  if (courses.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="mb-2 text-lg font-medium text-gray-900">{t('tabs.courses')}</p>
        <p className="mb-6 text-gray-500">You have not purchased any courses yet.</p>
        <Link to="/courses" className="text-orange-600 hover:underline">
          Browse courses
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {courses.map((c) => {
        const title = getLocalizedTitle(c, i18n.language);
        return (
          <Link
            key={c.activityId}
            to={`/courses/${c.slug}/watch`}
            className="group overflow-hidden rounded-xl border border-gray-200 transition hover:shadow-md"
          >
            <div className="aspect-video w-full bg-gray-100">
              {c.coverImage && (
                <img src={c.coverImage} alt={title} className="h-full w-full object-cover" />
              )}
            </div>
            <div className="flex items-center gap-2 p-4">
              <PlayCircle className="h-5 w-5 text-orange-500" />
              <span className="font-medium text-gray-900 group-hover:text-orange-600">{title}</span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
